/**
 * TenantBrandShell — overrides the platform-default brand with the
 * signed-in tenant's brand_config_json for the duration of the
 * authenticated app.
 *
 * Architecture (Director, 2026-05-10):
 *   Outside the app (LandingPage, LoginPage, UnauthorizedPage)
 *     → platform brand (ReadyRoomOps). Set by the outer
 *       <BrandProvider> mounted in App.tsx with no config prop.
 *   Inside /app/* (after RequireAuth resolves a session)
 *     → tenant brand. This shell calls useTenantBrand() and mounts a
 *       nested <BrandProvider> with the resolved config, which takes
 *       precedence in the React context lookup. The inner provider's
 *       useEffect also rewrites the CSS custom properties on
 *       document.documentElement so descendants reading
 *       var(--brand-*) see the tenant values.
 *
 * Re-applies on sign-out: when RequireAuth unmounts the /app subtree
 * the inner provider unmounts and the outer's CSS vars (platform
 * defaults) take effect again on the next paint. The outer provider's
 * useEffect doesn't re-run on every render — it only re-runs when
 * its `config` prop reference changes — so we rely on the inner
 * provider's `[config]` dep firing once on its mount.
 *
 * For consistency we set the document title and favicon here too via
 * the nested BrandProvider's effect; outside the shell the title falls
 * back to "ReadyRoomOps" via the platform-default BrandProvider.
 */

import { type ReactNode } from 'react'
import { BrandProvider }  from './BrandProvider'
import { useTenantBrand } from '@/hooks/useTenantBrand'

export function TenantBrandShell({ children }: { children: ReactNode }): ReactNode {
  const { brand } = useTenantBrand()
  return <BrandProvider config={brand}>{children}</BrandProvider>
}
